import type { SplotStepEquipment } from '../../api/vegApl/SPLotModel';

const normalizeText = (value?: string) => String(value ?? '').trim().toLowerCase();

export interface EquipmentValidationResult {
    valid: boolean;
    equipmentId?: string;
    error?: string;
}

/**
 * Validates scanned equipment against available equipments in current step.
 * If mandatory equipments exist, validates only against those.
 * Otherwise validates against all available equipments.
 */
export const validateEquipment = (
    scannedValue: string,
    equipments: SplotStepEquipment[]
): EquipmentValidationResult => {
    if (!scannedValue || !scannedValue.trim()) {
        return {
            valid: false,
            error: 'Equipment ID is required',
        };
    }

    if (!equipments || equipments.length === 0) {
        return {
            valid: false,
            error: 'No equipments available for this step',
        };
    }

    const mandatoryEquipments = equipments.filter((eq) => Boolean(eq?.is_mandatory));
    const equipmentsToValidate = mandatoryEquipments.length > 0 ? mandatoryEquipments : equipments;

    const matched = equipmentsToValidate.find(
        (eq) => normalizeText(eq?.equipment_id) === normalizeText(scannedValue)
    );

    if (!matched) {
        const allowedList = equipmentsToValidate
            .map((eq) => eq?.display_name || eq?.equipment_id)
            .filter(Boolean)
            .join(', ');
        return {
            valid: false,
            error: allowedList ? `Invalid equipment. Allowed: ${allowedList}` : 'Invalid equipment',
        };
    }

    return {
        valid: true,
        equipmentId: matched.equipment_id,
    };
};

/**
 * Get allowed equipment IDs for display (mandatory first, then others)
 */
export const getAllowedEquipmentIds = (equipments: SplotStepEquipment[]): string[] => {
    if (!equipments || equipments.length === 0) return [];

    const mandatory = equipments
        .filter((eq) => Boolean(eq?.is_mandatory))
        .map((eq) => eq?.equipment_id)
        .filter(Boolean) as string[];

    const optional = equipments
        .filter((eq) => !Boolean(eq?.is_mandatory))
        .map((eq) => eq?.equipment_id)
        .filter(Boolean) as string[];

    return [...mandatory, ...optional];
};
